import React from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';

interface WeeklyPlan {
  id: string;
  weekNumber: number;
  title: string;
  status: 'completed' | 'active' | 'upcoming';
  completedWorkouts: number;
  totalWorkouts: number;
  completionRate: number;
  highlights: string[];
  challenges?: string[];
  dates: {
    start: string;
    end: string;
  };
}

const MOCK_WEEKLY_PLANS: WeeklyPlan[] = [
  {
    id: 'week-1',
    weekNumber: 1,
    title: 'Foundation Building',
    status: 'completed',
    completedWorkouts: 3,
    totalWorkouts: 3,
    completionRate: 100,
    highlights: [
      'Completed first strength training session',
      'Established morning routine',
      'Learned proper squat form'
    ],
    dates: {
      start: 'Dec 2, 2024',
      end: 'Dec 8, 2024'
    }
  },
  {
    id: 'week-2',
    weekNumber: 2,
    title: 'Building Consistency',
    status: 'completed',
    completedWorkouts: 2,
    totalWorkouts: 3,
    completionRate: 67,
    highlights: [
      'Improved push-up count from 8 to 12',
      'Added 15-minute walks on off days',
      'Consistent with nutrition tracking'
    ],
    challenges: ['Missed Friday workout due to work'],
    dates: {
      start: 'Dec 9, 2024',
      end: 'Dec 15, 2024'
    }
  },
  {
    id: 'week-3',
    weekNumber: 3,
    title: 'Progressive Loading',
    status: 'completed',
    completedWorkouts: 3,
    totalWorkouts: 3,
    completionRate: 100,
    highlights: [
      'Increased weights across all exercises',
      'First full plank for 60 seconds',
      'Perfect week - all workouts completed!'
    ],
    dates: {
      start: 'Dec 16, 2024',
      end: 'Dec 22, 2024'
    }
  },
  {
    id: 'week-4',
    weekNumber: 4,
    title: 'Holiday Adaptation',
    status: 'completed',
    completedWorkouts: 2,
    totalWorkouts: 3,
    completionRate: 67,
    highlights: [
      'Maintained routine during holidays',
      'Tried new bodyweight variations',
      'Stayed active with family walks'
    ],
    challenges: ['Holiday schedule conflicts', 'Travel disruptions'],
    dates: {
      start: 'Dec 23, 2024',
      end: 'Dec 29, 2024'
    }
  }
];

const PlansReview: React.FC = () => {
  const router = useRouter();
  const params = useLocalSearchParams<{ filter?: string }>();
  const filter = params.filter;

  const completedPlans = MOCK_WEEKLY_PLANS.filter(plan => plan.status === 'completed');
  const avgCompletionRate = Math.round(
    completedPlans.reduce((sum, plan) => sum + plan.completionRate, 0) / completedPlans.length
  );

  return (
    <div className="clean-chat-container flex flex-col h-screen bg-white max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <button 
          onClick={() => router.back()}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold text-gray-900">
          {filter === 'archived' ? 'Completed Plans' : 'Plans Review'}
        </h1>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        
        {/* Summary Stats */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Progress Summary</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{completedPlans.length}</div>
              <div className="text-sm text-gray-600">Weeks Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{avgCompletionRate}%</div>
              <div className="text-sm text-gray-600">Avg Completion</div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white rounded-lg">
            <div className="text-sm text-gray-600 mb-2">Overall Progress</div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${avgCompletionRate}%` }}
              />
            </div>
          </div>
        </div>

        {/* Weekly Plans List */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Week by Week Review</h3>
          
          {completedPlans.reverse().map((plan) => (
            <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">Week {plan.weekNumber}: {plan.title}</h4>
                  <p className="text-sm text-gray-600">{plan.dates.start} - {plan.dates.end}</p>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    plan.completionRate >= 80 ? 'text-green-600' : 
                    plan.completionRate >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {plan.completionRate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {plan.completedWorkouts}/{plan.totalWorkouts} workouts
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    plan.completionRate >= 80 ? 'bg-green-500' : 
                    plan.completionRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${plan.completionRate}%` }}
                />
              </div>
              
              {/* Highlights */}
              <div className="mb-3">
                <h5 className="text-sm font-medium text-gray-800 mb-2">✨ Highlights</h5>
                <ul className="space-y-1">
                  {plan.highlights.map((highlight, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start space-x-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Challenges */}
              {plan.challenges && plan.challenges.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-gray-800 mb-2">⚡ Challenges</h5>
                  <ul className="space-y-1">
                    {plan.challenges.map((challenge, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start space-x-2">
                        <span className="text-orange-500 mt-1">•</span>
                        <span>{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>
          ))}
        </div>
        
        {/* Insights & Patterns */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-800 mb-3">📊 Insights & Patterns</h3>
          <div className="space-y-2 text-sm text-yellow-700">
            <p>• <strong>Consistency:</strong> You've maintained {avgCompletionRate}% completion rate</p>
            <p>• <strong>Strength:</strong> Week 3 was your strongest with 100% completion</p>
            <p>• <strong>Adaptation:</strong> You successfully navigated holiday challenges</p>
            <p>• <strong>Growth:</strong> Progressive improvements in strength and endurance</p>
          </div>
        </div>
        
        {/* Continue Journey Button */}
        <div className="pt-4">
          <button 
            onClick={() => router.push('/mypath')}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            Continue Current Week →
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlansReview; 